/**
 * Reports hub, redesigned 2026-09-16 on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/file/dashboard/quick-access.tsx
 *   a titled section over a grid of clickable item cards, one per business area;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the coloured icon tile at the head of each card;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `components/detail-parts.tsx`.
 *
 * 008-reporting-dashboards T025 (US4, FR-016, SC-008). One place to find every report — this page
 * only links out, it fetches nothing of its own. Rows for `006`'s GST summary and `007`'s stock
 * movement report point at their existing routes rather than re-rendering them (FR-016/SC-008:
 * "exists exactly once"); the same is true of the four `US2`/`US3` reports (`005`006`007` T015-T021)
 * this feature already built at `/reports/*`, which had no shared index before this page existed.
 *
 * Each row is gated on the same permission its own route/nav.tsx entry already requires — a report
 * the caller can't open isn't offered, matching `dashboard.tsx`'s `can()`-filtered tile visibility
 * (FR-004's ethos, applied here to a link list rather than a tile grid). An area with nothing the
 * caller may open is left out whole, rather than shown as an empty heading.
 *
 * Each row gates on its own data route's permission (Sales and GST summary `billing.read`, Order pipeline and
 * Order-to-invoice `orders.read`, Packing variance and Weight-collision `packing.oversight`, Stale tier prices
 * `pricing.read`, Stock movement `inventory.read`).
 *
 * Each card's "Period report" / "Snapshot" cue restates what that report's own screen already does:
 * Sales, GST summary, Stock movement, Packing variance and Order-to-invoice take a from/to period;
 * Order pipeline, Weight-collision and Stale tier prices take none and answer as things stand now.
 */
import type { ReactNode } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  PiArrowRightBold as ArrowRight,
  PiArrowsDownUpDuotone as MovementIcon,
  PiArrowsLeftRightDuotone as MatchIcon,
  PiCalculatorDuotone as CalculatorIcon,
  PiCalendarBlankDuotone as CalendarIcon,
  PiChartBarDuotone as BarsIcon,
  PiChartLineUpDuotone as TrendIcon,
  PiChartPieSliceDuotone as HubIcon,
  PiClipboardTextDuotone as ClipboardIcon,
  PiClockDuotone as ClockIcon,
  PiCurrencyInrDuotone as RupeeIcon,
  PiHourglassDuotone as HourglassIcon,
  PiIntersectDuotone as OverlapIcon,
  PiScalesDuotone as ScalesIcon,
  PiTagDuotone as TagIcon,
  PiWarehouseDuotone as WarehouseIcon,
} from "react-icons/pi";
import { Badge, Text, Title } from "rizzui";
import { EmptyState, cn } from "@steelyard/theme";
import { CardTitle, PageHero } from "../../components/detail-parts";
import { can } from "../../lib/permissions";
import type { AccountSummary, Permission, PermissionState } from "../../types";

type AreaKey = "sales" | "orders" | "inventory" | "pricing";

/** One colour per business area. Tinted `-lighter`/`-dark` pairs, so every tile flips correctly in dark mode. */
const AREAS: readonly {
  key: AreaKey;
  title: string;
  hint: string;
  icon: ReactNode;
  tile: string;
  badge: "success" | "info" | "secondary" | "warning";
  hover: string;
  link: string;
}[] = [
  {
    key: "sales",
    title: "Sales & invoicing",
    hint: "What was invoiced, and the tax it carried.",
    icon: <RupeeIcon aria-hidden className="size-5" />,
    tile: "bg-green-lighter text-green-dark",
    badge: "success",
    hover: "hover:border-green",
    link: "text-green-dark",
  },
  {
    key: "orders",
    title: "Orders",
    hint: "Where orders stand, and where an invoice parted from its order.",
    icon: <ClipboardIcon aria-hidden className="size-5" />,
    tile: "bg-blue-lighter text-blue-dark",
    badge: "info",
    hover: "hover:border-blue",
    link: "text-blue-dark",
  },
  {
    key: "inventory",
    title: "Inventory & packing",
    hint: "Stock in and out, and what happened at the packing bench.",
    icon: <WarehouseIcon aria-hidden className="size-5" />,
    tile: "bg-secondary-lighter text-secondary-dark",
    badge: "secondary",
    hover: "hover:border-secondary",
    link: "text-secondary-dark",
  },
  {
    key: "pricing",
    title: "Pricing",
    hint: "Tier prices that no longer match what they were struck from.",
    icon: <TagIcon aria-hidden className="size-5" />,
    tile: "bg-orange-lighter text-orange-dark",
    badge: "warning",
    hover: "hover:border-orange",
    link: "text-orange-dark",
  },
];

type Cadence = "period" | "snapshot";

const REPORTS: readonly { label: string; description: string; href: string; permission: Permission; area: AreaKey; cadence: Cadence; icon: ReactNode }[] = [
  { label: "Sales", description: "Invoiced sales over a period, grouped by brand, category, customer, SKU or tier.", href: "/reports/sales", permission: "billing.read", area: "sales", cadence: "period", icon: <TrendIcon aria-hidden className="size-6" /> },
  { label: "GST summary", description: "Taxable value and tax by financial year, GST rate and tax head.", href: "/invoicing/gst-summary", permission: "billing.read", area: "sales", cadence: "period", icon: <CalculatorIcon aria-hidden className="size-6" /> },
  { label: "Order pipeline", description: "A live snapshot of every order by its current lifecycle state.", href: "/reports/pipeline", permission: "orders.read", area: "orders", cadence: "snapshot", icon: <BarsIcon aria-hidden className="size-6" /> },
  { label: "Order-to-invoice variance", description: "Where an invoiced total differs from the order it was raised from, and why.", href: "/reports/order-to-invoice", permission: "orders.read", area: "orders", cadence: "period", icon: <MatchIcon aria-hidden className="size-6" /> },
  { label: "Stock movement", description: "Every receipt, decrement, adjustment, reversal and reservation change across the warehouse.", href: "/reports/stock-movement", permission: "inventory.read", area: "inventory", cadence: "period", icon: <MovementIcon aria-hidden className="size-6" /> },
  { label: "Packing variance", description: "Amber and red packing events, broken down by packing operative, SKU and reason.", href: "/reports/variance", permission: "packing.oversight", area: "inventory", cadence: "period", icon: <ScalesIcon aria-hidden className="size-6" /> },
  { label: "Weight-collision", description: "SKU pairs whose weights overlap closely enough to force a packing confirmation.", href: "/reports/collisions", permission: "packing.oversight", area: "inventory", cadence: "snapshot", icon: <OverlapIcon aria-hidden className="size-6" /> },
  { label: "Stale tier prices", description: "Tier prices whose derived rate has drifted from the base price it was struck from.", href: "/reports/staleness", permission: "pricing.read", area: "pricing", cadence: "snapshot", icon: <HourglassIcon aria-hidden className="size-6" /> },
];

const CADENCE: Record<Cadence, { label: string; icon: ReactNode }> = {
  period: { label: "Period report", icon: <CalendarIcon aria-hidden className="size-3.5" /> },
  snapshot: { label: "Snapshot", icon: <ClockIcon aria-hidden className="size-3.5" /> },
};

export default function ReportsIndexPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const visible = REPORTS.filter((r) => can(permissionState, r.permission));

  return (
    <div className="@container flex flex-col gap-8">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Reports" }]}
        icon={<HubIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Reports"
        description="Every report your role can open, in one place. Pick one to run it."
        meta={
          <>
            <Badge variant="flat" color="info" rounded="pill" className="tabular-nums">
              {visible.length} {visible.length === 1 ? "report" : "reports"} available
            </Badge>
            <CadencePill cadence="period" hint="you choose the dates" />
            <CadencePill cadence="snapshot" hint="as things stand now" />
          </>
        }
      />

      {visible.length === 0 ? (
        <EmptyState title="No reports to show">No report is permitted for your role.</EmptyState>
      ) : (
        AREAS.map((area) => {
          const reports = visible.filter((report) => report.area === area.key);
          if (reports.length === 0) return null;
          return (
            <section key={area.key} aria-labelledby={`reports-${area.key}`} className="flex flex-col gap-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <Title as="h2" id={`reports-${area.key}`} className="text-lg font-semibold">
                    <CardTitle icon={area.icon} tile={area.tile}>
                      {area.title}
                    </CardTitle>
                  </Title>
                  <Text className="mt-1.5 text-sm text-gray-500">{area.hint}</Text>
                </div>
                <Badge variant="flat" color={area.badge} rounded="pill" className="tabular-nums">
                  {reports.length} {reports.length === 1 ? "report" : "reports"}
                </Badge>
              </div>

              <div className="grid gap-4 @lg:grid-cols-2 @5xl:grid-cols-3">
                {reports.map((report) => (
                  <Link
                    key={report.href}
                    to={report.href}
                    className={cn(
                      "group flex h-full flex-col rounded-xl border border-muted bg-gray-0 p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:bg-gray-50",
                      area.hover,
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className={cn("flex size-12 shrink-0 items-center justify-center rounded-xl", area.tile)}>{report.icon}</span>
                      <Badge variant="outline" rounded="pill" size="sm" className="gap-1 border-muted text-gray-600">
                        {CADENCE[report.cadence].icon}
                        {CADENCE[report.cadence].label}
                      </Badge>
                    </div>
                    <Title as="h3" className="mt-4 text-base font-semibold text-gray-900">
                      {report.label}
                    </Title>
                    <Text className="mt-1 flex-1 text-sm leading-relaxed text-gray-500">{report.description}</Text>
                    <span className={cn("mt-4 inline-flex items-center gap-1.5 text-sm font-semibold", area.link)}>
                      Open report
                      <ArrowRight aria-hidden className="size-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}

function CadencePill({ cadence, hint }: { cadence: Cadence; hint: string }) {
  return (
    <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-600">
      {CADENCE[cadence].icon}
      <span>
        <span className="font-semibold text-gray-700">{CADENCE[cadence].label}</span> — {hint}
      </span>
    </Badge>
  );
}
